// This file will contain all the typescript types related to TelosAir product.

export type ScrollContainerAttribute = {
  loaded: boolean;
  height: null | number;
  width: null | number;
};

export type Parameter = 'CI' | 'AQI' | 'TEMP' | 'RH' | 'PRES' | 'CO₂' | 'PM2.5' | 'VOC' | 'PN2.5';

export type SensorData = {
  property_id: string;
  property_name: string;
  property_value: number;
  priority: string;
};

export type Client = {
  id: number;
  clientName: string;
  orgUrl: string | null;
  industry: string | null;
  plan: string | null;
  is_deleted?: boolean;
};

export type GroupAvg = {
  id: string;
  priority: string;
  property_value: number;
  property_name: string;
};

export type Device = {
  device_id: string;
  device_name: string;
  device_priority: string;
  device_data: SensorData[];
  gateWayId?: string;
  groupId?: string;
  config?: string;
  tags?: string[];
  purpose: string;
  position?: string;
  coordinates?: string;
};

export type DeviceGroup = {
  group_id: string;
  group_name: string;
  group_location: string;
  group_avg_value: GroupAvg[];
  devices: Device[];
};

export type DeviceDetails = {
  deviceName: string;
  id: number;
  model: string;
  status?: boolean;
  tagged?: boolean;
  dataRate: number;
  gatewayId: number;
  connectionStatus: string;
  configuration: string;
};

export type GatewayDetails = {
  gatewayName: string;
  id: number;
  softwareVersion: string;
  gatewayId: string;
  facilityid: string;
};

export type RoleTypes = 'ROLE_SYSTEM_ADMIN' | 'ROLE_USER' | 'ROLE_COMPANY_ADMIN';
export type UserDetails = {
  id: number;
  externalId: string;
  firstName: string;
  lastName: string;
  role: RoleTypes;
  status: string;
  userName: string;
  clientId: number;
  lastActive: string;
  userEmail: string;
};

export type UpdateUserAttribute = {
  id?: number;
  firstName?: string;
  lastName?: string;
  role?: RoleTypes;
  status?: string;
  userName?: string;
  clientId?: number;
};

export type ClientAttribute = {
  id: number;
  clientName: string;
  industry: string;
  ownerEmail: string;
  status: string;
  orgUrl: string;
  plan: string;
};

export type ParametersType =
  | 'ci'
  | 'aqi'
  | 'pressure'
  | 'pn03_m'
  | 'temp'
  | 'hum'
  | 'co2'
  | 'tvoc'
  | 'pm25_m'
  | 'pm100_m'
  | 'ch4'
  | 'so2'
  | 'co'
  | 'no2'
  | 'o3';

export type ParticleData = { [key in ParametersType]: number };

export type DataWidgetMinMaxAttribute = { min: ParticleData; max: ParticleData };

export type DeviceSummaryAttribute = {
  device: string;
  groupId: number;
  parameters: ParticleData;
};

/**
 * Interface for extended version of particle data.
 */
export interface ParticleDataWithSerialNumber extends ParticleData {
  serialNumber: number;
}

export type ConfigAttributes = {
  clients: string[];
  zones: string[];
  rooms: string[];
  facility: string[];
  sensors: number[];
  sensorNumber: string[];
};

// eslint-disable-next-line no-shadow
export enum Parameters {
  tvoc = 'tvoc',
  pressure = 'pressure',
  temp = 'temp',
  pm25_m = 'pm25_m',
  co2 = 'co2',
  hum = 'hum'
}

export type GraphData = {
  data: { data: number[][]; name: ParametersType }[];
  xAxis: number[];
};

export type summaryAPIProps = {
  startDate: number;
  endDate: number;
  /** Histogram bucket width, e.g. "15m"; the server auto-picks when omitted. */
  dataPointsDifference?: string;
};

export type AlertRule = {
  id: number;
  alertName?: string;
  alertDescription: string;
  priority: string;
  createdBy?: string;
  createdDate: Date;
  parameterName: string;
  aggType?: string;
  timeUnit?: string;
  timeValue?: string;
  filter?: null | any; // Replace 'any' with the actual type of 'filter' if possible
  notification?: string;
  groupBy?: string;
  parameterValue?: string;
  clientName?: string;
  operator?: string;
  status?: string;
  mobileNumber?: string;
  reportDataMissing?: string;
  deviceId?: string;
  alertType?: string;
  fromValue?: string;
  toValue?: string;
  alertSupressionTime?: string;
  alertSupressionCount?: string;
  alertState?: null | any; // Replace 'any' with the actual type of 'alertState' if possible
  startDate?: string;
  endTime?: string;
  dayOfWeek?: string;
};

export type TimeSpanDateAttributes = {
  startDate: number;
  endDate: number;
};

export type TempAlertRule = {
  priority: string;
  parameter: string;
  selected_device: any;
  lower_threshold: string;
  higher_threshold: string;
  aggregation: string;
  email: string;
  aggregation_type: string;
};

// eslint-disable-next-line no-shadow
export enum TimeSpan {
  RealTime = 'Real Time',
  Mins15 = 'Last 15 mins',
  Hr1 = 'Last 1 hr',
  Hrs4 = 'Last 4 hrs',
  Hrs24 = 'Last 24 hrs',
  Week1 = 'Last 1 week',
  Month1 = 'Last 1 month',
  Custom = 'Custom'
}

// eslint-disable-next-line no-shadow
export enum PriorityList {
  critical = 'critical',
  high = 'high',
  medium = 'medium',
  low = 'low',
  none = '',
  outOfRange = 'outOfRange'
}

// eslint-disable-next-line no-shadow
export enum DeviceType {
  OUTDOOR = 'OUTDOOR',
  INDOOR = 'INDOOR',
  MIXED = 'MIXED'
}

export type DeviceAttributes = {
  id: number;
  serialNumber: number;
  type: number;
  purpose: string;
  version: string;
  tags: null | string[];
  deviceName: string;
  deviceType: DeviceType;
  status: 'active' | 'inactive';
};

export type DataRate = {
  working: number;
  moderate: number;
  default: number;
};

export type DataRateAtrribute = {
  value: string;
  state: 'working' | 'moderate' | 'default';
};

export type DataRateDeviceWise = {
  [key: string]: DataRateAtrribute;
};

export type DeviceAttributesV1 = {
  id: number;
  serial_number: number;
  deviceName: string;
  client: string;
  facility: string;
  type: number;
  zone: string;
  room: string;
  x: number;
  y: number;
  floorPlan: string;
  sensorStatus: any;
  location: string;
  sparkLines: number[];
  lastSeen: number;
  firstSeen: number;
  timeZone: string;
  particleData?: any;
  updatedDate: string;
};

export type GroupedDeviceAttributes = {
  data: DeviceAttributes[];
  groupLocation: string;
  groupImage: string;
};

export type GroupedDeviceDataAttributes = {
  [key: string]: GroupedDeviceAttributes;
};

/**
 * ExtendedDeviceAttributes interface.
 */
export interface ExtendedDeviceAttributes extends DeviceAttributes {
  active: boolean;
  tagged?: boolean;
}

export type UserConfigV2 = {
  id: number | null;
  userId: number | null;
  config: any;
};

export type RegisterUser = {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  orgName: string;
};

export type DetailedChartDataResponse = {
  data: {
    detailed: {
      data: number[];
      name: number;
    }[];
    status: boolean;
    joinNulls: boolean;
  };
  timeSeriesType: string;
};

export type MultiAxisChartProps = {
  device: string;
  data: number[][];
};

export type AddDeviceV2Props = {
  id: null | number;
  serialNumber: number;
  type: string;
  purpose: string;
  version: string;
  tags: null | string[];
  deviceName: string;
  deviceType: DeviceType;
};

export type UserConfig = {
  username?: string;
  pmType?: string; // 'ug/m^3'
  pnType?: string; // '#/0.1L'
  pressure?: string; // "H2O"
  relativeHumidity?: string; // '%'
  temperature?: string; // '℃'
  tvoc?: string; // 'ppb'
  co2?: string; // 'ppm'
  highResTime?: string;
  lowResTime?: string;
  timeZone?: string; // 'America/New_York'
  liveRefreshTime?: string; // '30000'
  sensorStatusTime?: string;
  tempThreshold?: string;
  pressureThreshold?: string;
  vocsThreshold?: string;
  reativeHumidityThreshold?: string;
  pressureMax?: string;
  pressureMin?: string;
  tvocMin?: string;
  tvocMax?: string;
  co2Min?: string;
  co2Max?: string;
  humidityMax?: string;
  humidityMin?: string;
  tempMin?: string;
  tempMax?: string;
  pn10Min?: string;
  pn10Max?: string;
  pn25Min?: string;
  pn25Max?: string;
  pm25Min?: string;
  pm25Max?: string;
  pn03Min?: string;
  pn03Max?: string;
  pm100Min?: string;
  pm100Max?: string;
  constantForMeanCalculation?: string;
  alertEmail?: string;
  particleConThreshold?: string;
  co2Threshold?: string;
  pm25Threshold?: string;
  pm10Threshold?: string;
};

/**
 * Interface for DeviceListWithParticleDataAttributes.
 */
export interface DeviceListWithParticleDataAttributes extends DeviceAttributes {
  particleData: ParticleData;
}

export type DeviceLocation = {
  [key: string]: {
    x: number;
    y: number;
  };
};

export type Group = {
  floorPlan: string;
  groupId: number;
  groupName: string;
  lastModifiedDate: string;
  sensorDetailsList: DeviceAttributesV1[];
  type?: 'indoor' | 'outdoor' | 'mixed';
};

export type ActionsTypes =
  | 'edit'
  | 'delete'
  | 'change-status'
  | 'remove'
  | 'reset'
  | 'resend-invite';

export type ActionData<T> = {
  type: ActionsTypes;
  data: T;
};

// eslint-disable-next-line no-shadow
export enum AccessLevels {
  full = 'full',
  view = 'view',
  none = 'none'
}
export type RoleAttributes = {
  type: string;
  data: {
    type: string;
    permissions: {
      owner: AccessLevels;
      administrator: AccessLevels;
      operator: AccessLevels;
      observer: AccessLevels;
    };
  }[];
};

export type Option<T> = {
  name: string;
  id: T;
  childOptions?: {
    name: string;
    id: T;
  }[];
};

export type DashboardDataFilter = { type: 'device' | 'group'; id: number; name: string };

/**
 * Represents the state of unit settings.
 * @typedef {object} UnitsState
 * @property {string | undefined} temperature - The selected temperature unit.
 * @property {string | undefined} pnType - The selected PN type unit.
 * @property {string | undefined} pmType - The selected PM type unit.
 * @property {string | undefined} co2 - The selected CO2 unit.
 * @property {string | undefined} tvoc - The selected TVOC unit.
 * @property {string | undefined} relativeHumidity - The selected relative humidity unit.
 * @property {string | undefined} pressure - The selected pressure unit.
 */

export type LineChartGraphDataAttributes = {
  value: number[][];
  bordercolor: string;
};

export type AlertStatus = 'pending' | 'dismiss' | 'resolved' | 'all';

export type AlertTag = {
  addedBy: string;
  createdDate: Date;
  id: string;
  title: string;
};

export type Alert = {
  alertId: number;
  serialNumber: string;
  timestamp: Date;
  parameter: ParametersType;
  endDate: Date;
  avgValue: number;
  id: string;
  status: AlertStatus;
  name: string;
  sort: number[];
  tags: null | string[];
  notes: null | AlertTag[];
  activities: null;
};

export type summaryOverTimeResponse = {
  data: { data: number[][]; name: ParametersType }[];
  xAxis: number[];
};

export type ParameterAttribute = {
  icon: string;
  color: string;
  parameterName: ParametersType;
  displayName: string;
  shortName: string;
  possibleUnits: string[];
  unit: string;
  id: number;
  order: number;
  /**
   * Whether the client shows this parameter. Undefined means enabled - rows
   * created before the flag existed must keep showing up. Toggled in
   * Settings -> Parameters -> Layout.
   */
  isEnabled?: boolean;
};

export type EventTypes = 'EVENTS' | 'NOTES';

export type EventAttribute = {
  id: number;
  eventNotes: string;
  eventTime: number;
  tags: string[];
  comments: string;
  eventType: EventTypes;
  createdByUser?: string;
  createdDate?: string;
  devices: string[];
  groups: number[];
};

export type Notificationresponse = {
  id: number;
  facilityId: number;
  events: string;
  notificationDestination: string;
  faultDetectionEnabled: boolean;
  notifyResolvedIssue: boolean;
};
// eslint-disable-next-line no-shadow
export enum AwsLoginErrors {
  unconfirmed = 'unconfirmed',
  other = 'other',
  invalidCredentials = 'invalidCredentials',
  newUser = 'newUser',
  notFound = 'notFound',
  codeMismatch = 'codeMismatch',
  expiredCode = 'expiredCode'
}

export type AwsLoginErrorAttributes = AwsLoginErrors | false;

export type LoginCallBackDataProps = {
  token?: any;
  refreshToken?: string;
  accessToken?: string;
};

export type AuthAttribute = { error?: AwsLoginErrorAttributes; data?: any };

export type ThresholdValues = {
  lowerWarning: number;
  lowerCritical: number;
  upperWarning: number;
  upperCritical: number;
  min: number;
  max: number;
  incomplete?: boolean;
};

export type ThresholdConfig = {
  name: string;
  id: string;
  created: Date | null;
  updated: Date | null;
  createdBy: string | 'auto';
  description: string;
  inputVisible?: boolean;
  mode: 'auto' | 'copy' | 'custom';
  isNew?: boolean;
  thresholds: {
    [key in ParametersType]: ThresholdValues;
  };
};

export type ClientThresholdSettingsAttribute = {
  userCreatedConfigs: ThresholdConfig[];
  indoorConfigId: string;
  outdoorConfigId: string;
  mixedConfigId: string;
};

export type GraphPointStyles =
  | 'circle'
  | 'rect'
  | 'rectRounded'
  | 'rectRot'
  | 'cross'
  | 'crossRot'
  | 'star'
  | 'triangle';

export type GraphPointStyleIcon = Record<GraphPointStyles, string>;
export type GraphLegend = {
  label: string;
  value: string;
  pointStyle?: GraphPointStyles;
  pointRadius?: number;
  color: string;
  active: boolean;
};
export type GraphLegendGroups = Record<string, GraphLegend[]>;

export type LegendAttributes = {
  isGroupped: boolean;
  grouppedLegends: GraphLegendGroups;
  legends: { key: string; label: string; color: string; display?: boolean }[];
};

export type GraphSummaryData = {
  name: ParametersType;
  data: [number, number][];
};

export type MultiDeviceSummaryType = Record<string, GraphSummaryData[]>;

export type PlotDataType = {
  timestamp: number;
  parameters: Record<ParametersType, number>;
};

export type PlotTransformedDataType = Record<string, PlotDataType[]>;

export type SimplifiedParameterWidget = {
  value: string | number;
  label: string;
  description: string;
  subValue?: string;
  subValueColor?: string;
  status: PriorityList;
  shortName: string;
  unit: string;
};


/** A sensor that is due for calibration. */
export type CalibrationReminder = {
  serialNumber: number;
  deviceName: string;
  /** ISO date the device was added to the account. */
  onboardedDate: string;
  /** ISO date of the most recent calibration, or null if never calibrated. */
  lastCalibratedDate: string | null;
  /** ISO date calibration became due: a year after onboarding or last calibration. */
  dueSince: string;
  /** Set while the reminder is dismissed; null when it is showing. */
  snoozedUntil: string | null;
};

/**
 * One stored calibration coefficient. Readings from `effectiveDate` onward are
 * served as `value * slope + intercept`.
 */
export type CalibrationRecord = {
  id: number;
  serialNumber: number;
  parameter: ParametersType;
  slope: number;
  intercept: number;
  effectiveDate: string;
  notes: string | null;
};

/** Outcome of a calibration CSV upload. */
export type CalibrationUploadResult = {
  /** Coefficients stored (inserted or updated). */
  applied: number;
  /** One message per skipped row, naming the line number. */
  errors: string[];
  /** Serial numbers touched by the upload. */
  devices: number[];
};
